import { useContext } from "react";
import { IconButton, TextCard } from "@livingmap/hippo";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import { LanguageConfig } from "redux/services/config";

import { useClickOutside } from "hooks/useClickOutside";

import AnchoredDialog from "components/AnchoredDialog/AnchoredDialog";
import { MapContext } from "components/Map/Map";
import Button from "components/Button/Button";

import i18n from "i18n";

import styles from "./LanguageSelect.module.scss";

export interface LanguageSelectProps {
  onClose: () => void;
  onButtonClick: () => void;
  onLanguageChange: (id: string) => void;
  isDialogOpen?: boolean;
  languages: LanguageConfig[];
}

export default function LanguageSelect({
  onButtonClick,
  onClose,
  onLanguageChange,
  isDialogOpen,
  languages,
}: LanguageSelectProps) {
  const { controlTheme } = useContext(MapContext);
  const { t } = useTranslation();

  useClickOutside(styles.container, onClose);

  return (
    <div className={styles.container}>
      <Button
        dataQA="language-select-button"
        onClick={onButtonClick}
        label={
          languages.find(({ id }) => id === i18n.language)
            ?.human_readable_language || t("language_select.button_text")
        }
        color="black"
        {...(controlTheme === "light" && {
          outlined: true,
        })}
        leftIcon="GlobeIcon"
        rounded
        theme={controlTheme}
      />
      {isDialogOpen && (
        <AnchoredDialog
          className={styles.dialog}
          dataQA="language-select-dialog"
        >
          <div
            data-qa="language-select-dialog-content"
            className={classNames(styles.dialogContent, styles[controlTheme])}
          >
            <div className={styles.header}>
              <p>{t("language_select.dialog_title")}</p>
            </div>
            <div className={styles.body}>
              {languages.map(
                ({ id, human_readable_language, human_readable_region }) => (
                  <TextCard
                    key={`${id}-${human_readable_language}`}
                    title={human_readable_language}
                    subtitle={human_readable_region}
                    dataQA={`${id}-${human_readable_language}`}
                    active={i18n.language === id}
                    onClick={() => onLanguageChange(id)}
                  />
                ),
              )}
            </div>
            <IconButton
              dataQA="language-select-close-button"
              iconSize={24}
              size="regular"
              type="primary"
              icon="close"
              className={styles.closeBtn}
              onClick={onClose}
            />
          </div>
        </AnchoredDialog>
      )}
    </div>
  );
}
